import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search } from 'lucide-react'
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import { useUser } from '@/contexts/UserContext'
import { getAllNavDestinations, type NavDestination } from '@/config/navigationConfig'
import { useFavorites } from '@/hooks/use-favorites'
import { useRecentRoutes } from '@/hooks/use-recent-routes'

/**
 * Global command palette (Cmd/Ctrl+K). Type to jump to any page the current
 * user can access, or hand a query off to record search. Favorites and recent
 * destinations surface first when the query is empty.
 */
export function CommandPalette() {
  const navigate = useNavigate()
  const { effectiveUserType } = useUser()
  const { favorites } = useFavorites()
  const recents = useRecentRoutes()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  // Cmd/Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setOpen((o) => !o)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const destinations = useMemo(() => getAllNavDestinations(effectiveUserType), [effectiveUserType])
  const byHref = useMemo(
    () => new Map(destinations.map((d) => [d.href, d] as const)),
    [destinations]
  )

  const favoriteDests = useMemo(
    () => favorites.map((h) => byHref.get(h)).filter((d): d is NavDestination => Boolean(d)),
    [favorites, byHref]
  )
  const recentDests = useMemo(
    () => recents.map((h) => byHref.get(h)).filter((d): d is NavDestination => Boolean(d)),
    [recents, byHref]
  )

  // Destinations grouped by their owning nav group, preserving config order.
  const grouped = useMemo(() => {
    const map = new Map<string, NavDestination[]>()
    for (const d of destinations) {
      const list = map.get(d.groupLabel)
      if (list) list.push(d)
      else map.set(d.groupLabel, [d])
    }
    return Array.from(map.entries())
  }, [destinations])

  const trimmed = query.trim()
  const isEmpty = trimmed.length === 0

  const run = (action: () => void) => {
    setOpen(false)
    setQuery('')
    action()
  }

  const renderItem = (dest: NavDestination, prefix: string) => {
    const Icon = dest.icon
    return (
      <CommandItem
        key={`${prefix}:${dest.href}`}
        value={`${prefix} ${dest.label} ${dest.groupLabel} ${dest.href}`}
        onSelect={() => run(() => navigate(dest.href))}
      >
        <Icon className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{dest.label}</span>
        {dest.groupLabel !== dest.label && (
          <span className="ml-auto text-xs text-muted-foreground">{dest.groupLabel}</span>
        )}
      </CommandItem>
    )
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Jump to a page or search"
      description="Type to navigate to any page you can access, or search your records."
    >
      <CommandInput
        placeholder="Jump to a page or search records…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {trimmed.length > 1 && (
          <CommandGroup heading="Search">
            <CommandItem
              value={trimmed}
              onSelect={() => run(() => navigate(`/search?q=${encodeURIComponent(trimmed)}`))}
            >
              <Search className="mr-2 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>Search records for &ldquo;{trimmed}&rdquo;</span>
            </CommandItem>
          </CommandGroup>
        )}

        {isEmpty && favoriteDests.length > 0 && (
          <CommandGroup heading="Favorites">
            {favoriteDests.map((d) => renderItem(d, 'fav'))}
          </CommandGroup>
        )}

        {isEmpty && recentDests.length > 0 && (
          <CommandGroup heading="Recent">
            {recentDests.map((d) => renderItem(d, 'recent'))}
          </CommandGroup>
        )}

        {grouped.map(([groupLabel, items]) => (
          <CommandGroup key={groupLabel} heading={groupLabel}>
            {items.map((d) => renderItem(d, 'page'))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  )
}
